import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { matrimonyAPI } from '../../utils/api';

export default function Payment() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('consultation'); // 'consultation' or 'full'
  const [txId, setTxId] = useState('');
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const plans = {
    consultation: {
      id: 'consultation',
      name: 'Consultation Membership',
      price: '21,000',
      numericPrice: '21000',
      duration: '6 Months Match Search Access',
      badge: 'Popular',
      details: 'Includes dedicated consultation and curated match search access for 6 months.'
    },
    full: {
      id: 'full',
      name: 'Full Membership',
      price: '50,000',
      numericPrice: '50000',
      duration: 'Unlimited Match Search Period',
      badge: 'Best Value',
      details: 'Includes lifetime consultation, priority matchmaking, and unlimited period match search access.'
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshotFile(file);
      setScreenshotPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!txId.trim() && !screenshotFile) {
      setError('Please provide either a Transaction ID or upload a receipt screenshot.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const currentPlanObj = plans[selectedPlan];
      const formData = new FormData();
      formData.append('plan_type', selectedPlan);
      formData.append('amount', currentPlanObj.numericPrice);
      formData.append('currency', 'NPR');
      formData.append('payment_method', 'siddhartha_bank_qr');
      formData.append('transaction_id', txId.trim() || 'RECEIPT_IMAGE');
      formData.append('notes', `Payment for ${currentPlanObj.name} (NPR ${currentPlanObj.price})`);
      if (screenshotFile) {
        formData.append('screenshot', screenshotFile);
      }

      await matrimonyAPI.submitPayment(formData);
      setSubmitted(true);
    } catch (err) {
      setError(err?.detail || err?.error || 'Could not submit payment record. Please WhatsApp Laxmikant Timilsina directly at 9857074103.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sec-wrapper" style={{ maxWidth: '920px' }}>
      {/* Hero Banner */}
      <section className="hero-section">
        <div className="hero-content">
          <div style={{ marginBottom: '1.2rem' }}>
            <span className="badge-hero-top">
              ✦ CONFIDENTIAL MEMBERSHIP & PAYMENT PORTAL ✦
            </span>
          </div>

          <h1 className="hero-title">
            Marriage Consultation & <span className="hero-title-gradient">Membership QR Payment</span>
          </h1>

          <p className="hero-subtitle">
            Registration is <strong>100% FREE</strong>. Upgrade to Consultation or Full Membership for curated match search.
            Direct account managed by <strong>Laxmikant Timilsina (9857074103)</strong> | Office: <strong>Yogikuti, Butwal</strong>.
          </p>

          {user && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: 'rgba(232, 245, 233, 0.95)', color: '#1b5e20', padding: '0.45rem 1.2rem',
              borderRadius: '9999px', fontSize: '0.82rem', fontWeight: 800, marginTop: '0.4rem'
            }}>
              ✓ Logged in as <strong>{user.full_name || user.username}</strong>
            </div>
          )}
        </div>
      </section>

      {/* Free Registration Notification */}
      <div style={{
        background: '#e8f5e9',
        border: '1.5px solid #81c784',
        borderRadius: '16px',
        padding: '1.2rem 1.5rem',
        marginBottom: '2rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ fontSize: '2.4rem' }}>🎁</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1b5e20' }}>
            Profile Registration is Completely FREE (NPR 0)!
          </div>
          <div style={{ fontSize: '0.86rem', color: '#2e7d32', marginTop: '0.2rem' }}>
            You can create your complete marriage profile for free. Payment is only required when you choose to activate <strong>Consultation</strong> (6 Months Match Search) or <strong>Full Membership</strong> (Unlimited Match Search).
          </div>
        </div>
      </div>

      {/* Membership Plan Selection Cards */}
      <div style={{ marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#9E1B32', marginBottom: '1.2rem', fontFamily: 'var(--serif)', textAlign: 'center' }}>
          Choose Your Membership & Match Search Plan
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          {/* Consultation Card */}
          <div
            onClick={() => setSelectedPlan('consultation')}
            style={{
              background: selectedPlan === 'consultation' ? '#FFFDF9' : '#FFFFFF',
              border: `2px solid ${selectedPlan === 'consultation' ? '#9E1B32' : '#E8DFD1'}`,
              borderRadius: '20px',
              padding: '1.6rem',
              cursor: 'pointer',
              position: 'relative',
              boxShadow: selectedPlan === 'consultation' ? '0 10px 30px rgba(158,27,50,0.12)' : 'none',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
              <span className="badge" style={{ background: '#9E1B32', color: '#FFF' }}>
                Popular Choice
              </span>
              <input
                type="radio"
                name="planSelect"
                checked={selectedPlan === 'consultation'}
                onChange={() => setSelectedPlan('consultation')}
                style={{ accentColor: '#9E1B32', width: '20px', height: '20px' }}
              />
            </div>
            <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#1C1014', margin: '0 0 0.4rem' }}>
              Consultation Membership
            </h3>
            <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#9E1B32', marginBottom: '0.6rem' }}>
              NPR 21,000
            </div>
            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#8C6A23', marginBottom: '1rem', background: '#FAF6ED', padding: '0.4rem 0.8rem', borderRadius: '8px', display: 'inline-block' }}>
              ⏱️ Match search for 6 Months
            </div>
            <ul style={{ fontSize: '0.84rem', color: '#6E615E', paddingLeft: '1.2rem', margin: 0, lineHeight: '1.6' }}>
              <li>Full profile verification & badge</li>
              <li>Match search access for <strong>6 Months</strong></li>
              <li>Kundali & Horoscope evaluation</li>
              <li>Direct support with Laxmikant Timilsina</li>
            </ul>
          </div>

          {/* Full Membership Card */}
          <div
            onClick={() => setSelectedPlan('full')}
            style={{
              background: selectedPlan === 'full' ? '#FFFDF9' : '#FFFFFF',
              border: `2px solid ${selectedPlan === 'full' ? '#D4AF37' : '#E8DFD1'}`,
              borderRadius: '20px',
              padding: '1.6rem',
              cursor: 'pointer',
              position: 'relative',
              boxShadow: selectedPlan === 'full' ? '0 10px 30px rgba(212,175,55,0.18)' : 'none',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
              <span className="badge" style={{ background: '#D4AF37', color: '#1C1014', fontWeight: 800 }}>
                👑 Best Value
              </span>
              <input
                type="radio"
                name="planSelect"
                checked={selectedPlan === 'full'}
                onChange={() => setSelectedPlan('full')}
                style={{ accentColor: '#D4AF37', width: '20px', height: '20px' }}
              />
            </div>
            <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: '#1C1014', margin: '0 0 0.4rem' }}>
              Full Membership
            </h3>
            <div style={{ fontSize: '1.8rem', fontWeight: 900, color: '#8C6A23', marginBottom: '0.6rem' }}>
              NPR 50,000
            </div>
            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1b5e20', marginBottom: '1rem', background: '#e8f5e9', padding: '0.4rem 0.8rem', borderRadius: '8px', display: 'inline-block' }}>
              ♾️ Match search for UNLIMITED Period
            </div>
            <ul style={{ fontSize: '0.84rem', color: '#6E615E', paddingLeft: '1.2rem', margin: 0, lineHeight: '1.6' }}>
              <li>Full profile verification & premium badge</li>
              <li>Match search access for <strong>Unlimited Period</strong></li>
              <li>Priority family introduction & counselling</li>
              <li>Lifetime match updates from Laxmikant Timilsina</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Siddhartha Bank QR Payment Box */}
      <div className="card" style={{ textAlign: 'center', border: '2px solid #D4AF37', marginBottom: '2.5rem', boxShadow: '0 10px 30px rgba(212,175,55,0.15)', background: '#FFFFFF' }}>
        <span className="badge" style={{ marginBottom: '0.8rem', background: '#FAF6ED', color: '#8C6A23', border: '1px solid #D4AF37' }}>
          🏛️ Official Siddhartha Bank QR Code Payment
        </span>
        
        <h2 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#9E1B32', marginBottom: '0.3rem', fontFamily: 'var(--serif)' }}>
          Pay NPR {plans[selectedPlan].price} for {plans[selectedPlan].name}
        </h2>
        
        <p style={{ fontSize: '0.92rem', color: '#6E615E', marginBottom: '1.4rem' }}>
          Scan the Siddhartha Bank QR code below using any mobile banking or payment app (eSewa, Khalti, Fonepay, Banking Apps).
        </p>

        {/* QR Code Container */}
        <div style={{
          maxWidth: '340px',
          margin: '0 auto 1.5rem',
          background: '#fff',
          border: '2.5px solid #D4AF37',
          borderRadius: '24px',
          padding: '1.4rem',
          boxShadow: '0 12px 35px rgba(0,0,0,0.12)'
        }}>
          <img
            src="/siddhartha_qr.png"
            alt="Siddhartha Bank Payment QR Code - Laxmikant Timilsina"
            style={{ width: '100%', height: 'auto', borderRadius: '12px', display: 'block', margin: '0 auto' }}
          />

          <div style={{ marginTop: '1.2rem', padding: '0.8rem', background: '#FAF7F2', borderRadius: '14px', textAlign: 'left', border: '1px solid #E8DFD1' }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#9E1B32' }}>
              👤 Account Name: <strong>LAXMIKANT TIMILSINA</strong>
            </div>
            <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#1C1014', marginTop: '0.2rem' }}>
              🏦 Bank: <strong>Siddhartha Bank</strong> (PLATINUM SAVINGS)
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 900, color: '#8C6A23', marginTop: '0.2rem' }}>
              🔢 Account No: <strong>01315128317</strong>
            </div>
            <div style={{ fontSize: '0.82rem', color: '#6E615E', marginTop: '0.2rem' }}>
              📱 Mobile / eSewa: <strong>9857074103</strong>
            </div>
          </div>
        </div>

        <div style={{ background: '#FFF3E0', color: '#E65100', padding: '0.9rem 1.2rem', borderRadius: '14px', fontSize: '0.86rem', fontWeight: 700, maxWidth: '650px', margin: '0 auto 1.5rem', lineHeight: '1.5' }}>
          ⚠️ Instructions: Complete payment of <strong>NPR {plans[selectedPlan].price}</strong>. Take a screenshot of your payment receipt and submit it below or directly on WhatsApp.
        </div>

        <a
          href={`https://wa.me/9779857074103?text=Namaste%20Laxmikant%20ji,%20I%20have%20completed%20the%20payment%20of%20NPR%20${plans[selectedPlan].price}%20for%20${encodeURIComponent(plans[selectedPlan].name)}.%20Here%20is%20my%20receipt.`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.85rem 1.6rem' }}
        >
          💬 WhatsApp Receipt directly to 9857074103 →
        </a>
      </div>

      {/* Transaction & Receipt Submission Form */}
      <div className="card">
        <h3 style={{ fontSize: '1.45rem', fontWeight: 700, color: '#9E1B32', marginBottom: '0.6rem', fontFamily: 'var(--serif)' }}>
          Submit Receipt Proof for Account Activation
        </h3>
        <p style={{ fontSize: '0.86rem', color: '#6E615E', marginBottom: '1.4rem' }}>
          Upload your payment receipt screenshot or enter your transaction ID. Laxmikant Timilsina will review and activate your <strong>{plans[selectedPlan].name}</strong> ({plans[selectedPlan].duration}).
          {!user && <span style={{ color: '#9E1B32', fontWeight: 700 }}> You must be <button style={{ background: 'none', border: 'none', color: '#9E1B32', textDecoration: 'underline', cursor: 'pointer', fontWeight: 800, padding: 0 }} onClick={() => navigate('/consultation/login')}>logged in</button> to submit.</span>}
        </p>

        {error && (
          <div style={{ background: '#FEECEC', color: '#9E1B32', border: '1px solid rgba(158,27,50,0.3)', borderRadius: '14px', padding: '0.9rem 1.2rem', marginBottom: '1.2rem', fontSize: '0.88rem', fontWeight: 600 }}>
            ⚠️ {error}
          </div>
        )}

        {submitted ? (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '1.8rem', borderRadius: '16px', textAlign: 'center', border: '1.5px solid rgba(46,125,50,0.3)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>✅</div>
            <h4 style={{ fontSize: '1.35rem', fontWeight: 800, margin: '0 0 0.5rem 0' }}>Payment Proof Submitted Successfully!</h4>
            <p style={{ fontSize: '0.92rem', margin: '0 0 1rem 0', color: '#1b5e20' }}>
              Your proof for <strong>{plans[selectedPlan].name} (NPR {plans[selectedPlan].price})</strong> has been received by consultant <strong>Laxmikant Timilsina</strong>. Your account search access will be activated shortly.
            </p>
            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => navigate('/consultation/profile')}>
                View My Profile →
              </button>
              <a
                href={`https://wa.me/9779857074103?text=Namaste%20Laxmikant%20ji,%20I%20just%20submitted%20my%20payment%20proof%20for%20${encodeURIComponent(plans[selectedPlan].name)}%20on%20Vivah%20Nepal.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                💬 Notify on WhatsApp
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.4rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                Selected Package Plan:
              </label>
              <select
                className="form-input"
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                disabled={!user}
                style={{ fontWeight: 700 }}
              >
                <option value="consultation">Consultation Membership — NPR 21,000 (6 Months Search)</option>
                <option value="full">Full Membership — NPR 50,000 (Unlimited Search Period)</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.4rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                📸 Upload Payment Receipt Screenshot:
              </label>
              <input
                type="file"
                accept="image/*"
                className="form-input"
                onChange={handleFileChange}
                disabled={!user}
                style={{ padding: '0.5rem' }}
              />
              {screenshotPreview && (
                <div style={{ marginTop: '0.8rem', textAlign: 'center' }}>
                  <img
                    src={screenshotPreview}
                    alt="Receipt Screenshot Preview"
                    style={{ maxHeight: '220px', borderRadius: '14px', border: '2px solid #D4AF37', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}
                  />
                  <div style={{ fontSize: '0.76rem', color: '#2e7d32', fontWeight: 700, marginTop: '0.4rem' }}>✓ Image attached for review</div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.6rem' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                Siddhartha Bank / eSewa Transaction ID (Optional):
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. TXN-01315128317"
                value={txId}
                onChange={(e) => setTxId(e.target.value)}
                disabled={!user}
              />
            </div>

            <button
              className="btn btn-primary btn-full"
              type="submit"
              disabled={loading || !user}
              style={{ opacity: (!user || loading) ? 0.6 : 1, padding: '0.95rem', fontSize: '1rem', fontWeight: 700 }}
            >
              {loading ? '⏳ Submitting Payment Proof…' : `📤 Submit Receipt for NPR ${plans[selectedPlan].price} (${plans[selectedPlan].name}) →`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}